import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { AppDispatch, RootState } from '@/store';
import { update } from '@/store/storeSlice';
import { TreeItem } from '@/store/types';
import {
  Button,
  Input,
  Label,
  Select,
  SelectOption,
  Slider,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './FormOperations.scss';

export type TFormOperations = {
  OPERATION: TreeItem;
  OPERATION_TYPE_ID: TreeItem;
  STEP: TreeItem<number>;
  OPERATION_BUSINESS: TreeItem;
  OPERATION_SCRAP: TreeItem<number>;
  OPERATION_REWORK: TreeItem<number>;
  TS_FACTOR: TreeItem<number>;
};

/**
 * FormOperations Component
 * Manages the form for editing an operation details within a simulation scenario
 * Handles form submission, input changes, and closing the form drawer
 */
const FormOperations = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected === true),
  );
  const [scrapReworkEnabled, setScrapReworkEnabled] = useState(true);

  const WipArea = 'operations';

  const InitialFormProducts: TFormOperations = {
    OPERATION: { value: '', originalValue: '' },
    OPERATION_BUSINESS: { value: '', originalValue: '' },
    OPERATION_TYPE_ID: { value: '', originalValue: '' },
    STEP: { value: 0, originalValue: 0 },
    OPERATION_SCRAP: { value: 0, originalValue: 0 },
    OPERATION_REWORK: { value: 0, originalValue: 0 },
    TS_FACTOR: { value: 1, originalValue: 1 },
  };

  const [form, setForm] = useState(InitialFormProducts);

  /**
   * handleInputChange Function
   * Updates the form state when an input value changes
   * Handles different input types and ensures the form state is updated correctly
   */
  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { id, value } = event.target as HTMLInputElement & {
      id: string;
      value: string;
      type: string;
      checked: boolean;
    };
    const updateForm = (id: keyof TFormOperations, newValue: unknown) => {
      let v = newValue;
      if (typeof form[id] === 'number') {
        v = parseInt(v as string);
      }
      setForm((prevForm) => ({
        ...prevForm,
        [id]: {
          ...prevForm[id],
          value: v,
        },
      }));
    };

    updateForm(id as keyof TFormOperations, value);
  };

  const handleScrapReworkChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id, value } = event.target; // Get the slider ID and value
    const newValue = parseInt(value, 10);

    if (id === 'OPERATION_SCRAP') {
      setForm((prevForm) => ({
        ...prevForm,
        OPERATION_SCRAP: {
          ...prevForm.OPERATION_SCRAP,
          value: newValue,
        },
        OPERATION_REWORK: {
          ...prevForm.OPERATION_REWORK,
          value: 100 - newValue,
        },
      }));
    } else if (id === 'OPERATION_REWORK') {
      setForm((prevForm) => ({
        ...prevForm,
        OPERATION_REWORK: {
          ...prevForm.OPERATION_REWORK,
          value: newValue,
        },
        OPERATION_SCRAP: {
          ...prevForm.OPERATION_SCRAP,
          value: 100 - newValue,
        },
      }));
    }
  };

  const handleCloseDrawer = () => {
    setSimulationParams(
      produce((draft) => {
        draft.formItem = undefined;
        draft.formArea = undefined;
        draft.drawerOpen = false;
      }),
    );

    setForm(InitialFormProducts);
  };

  /**
   * handleSubmit Function
   * Handles the form submission event
   * Dispatches an update action to the Redux store with the form data
   * Closes the form drawer after submission
   *
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    dispatch(
      update({
        id: simulationParams.formItem,
        area: WipArea,
        content: form,
      }),
    );

    handleCloseDrawer();
  };

  const selectedItem =
    simulationParams.formItem && selectedScenario
      ? selectedScenario.data[WipArea].byId[simulationParams.formItem]
      : null;

  useEffect(() => {
    if (!selectedItem) return;

    const formToChange: TFormOperations = {
      OPERATION: {
        value: selectedItem.OPERATION.value,
        originalValue: selectedItem.OPERATION.originalValue,
      },
      OPERATION_BUSINESS: {
        value: selectedItem.OPERATION_BUSINESS.value,
        originalValue: selectedItem.OPERATION_BUSINESS.originalValue,
      },
      OPERATION_TYPE_ID: {
        value: selectedItem.OPERATION_TYPE_ID.value,
        originalValue: selectedItem.OPERATION_TYPE_ID.originalValue,
      },
      STEP: {
        value: parseInt(selectedItem.STEP.value),
        originalValue: parseInt(selectedItem.STEP.originalValue),
      },
      OPERATION_SCRAP: {
        value: parseInt(selectedItem.OPERATION_SCRAP.value),
        originalValue: parseInt(selectedItem.OPERATION_SCRAP.originalValue),
      },
      OPERATION_REWORK: {
        value: parseInt(selectedItem.OPERATION_REWORK.value),
        originalValue: parseInt(selectedItem.OPERATION_REWORK.originalValue),
      },
      TS_FACTOR: {
        value: parseFloat(selectedItem.TS_FACTOR.value),
        originalValue: parseFloat(selectedItem.TS_FACTOR.originalValue),
      },
    };
    setForm(formToChange);
  }, [selectedItem]);

  if (!selectedItem || !selectedScenario) return;

  type TSelect = {
    id: string;
    name: string;
  };

  const sites: TSelect[] = [];
  selectedScenario.data.sites.allIds.forEach((itemId: string) => {
    const item = selectedScenario.data.sites.byId[itemId];

    const newItem = {
      id: item.SITE_ID.originalValue,
      name: item.SITE.value,
    };

    sites.push(newItem);
  });
  const workplans: TSelect[] = [];
  selectedScenario.data.work_plans.allIds.forEach((itemId: string) => {
    const item = selectedScenario.data.work_plans.byId[itemId];

    const newItem = {
      id: item.WORK_PLAN_ID.originalValue,
      name: item.WORK_PLAN.value,
    };

    workplans.push(newItem);
  });

  const formMargin = 30;

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 20,
          paddingBottom: 100,
        }}
      >
        <Typography tag="h4" color="#8a00e5"> Edit Operation</Typography>
        <div
          style={{
            marginTop: formMargin / 2,
          }}
        ></div>
        <Label tag="textsmall_strong">Operation</Label>
        <Input
          size="small"
          id="OPERATION"
          defaultValue={form.OPERATION.value}
          onChange={(event) => handleInputChange(event)}
        ></Input>

        <Label marginTop={formMargin} tag="textsmall_strong">
          Operation ID
        </Label>
        <Input
          size="small"
          id="OPERATION_BUSINESS"
          defaultValue={form.OPERATION_BUSINESS.value}
          onChange={(event) => handleInputChange(event)}
        ></Input>
        <Label marginTop={formMargin} tag="textsmall_strong">
          TS Factor
        </Label>
        <Input
          size="small"
          id="TS_FACTOR"
          isNumberInput={true}
          defaultValue={form.TS_FACTOR.value.toString()}
          onChange={(event) => {
            if(parseFloat(event.target.value) < 0) {
              event.target.value = '1';
            }
            handleInputChange(event)}}
          placeholder="1.00"
        ></Input>
        <Label marginTop={formMargin} tag="textsmall_strong">
          Scrap/Rework
        </Label>
        <Select
          onChange={(event) =>
            setScrapReworkEnabled(event.target.value === 'yes')
          }
          size="small"
          defaultValue={'yes'}
          id="RESOURCE_SCRAP_REWORK_ID"
        >
          <SelectOption label="Yes" value="yes" />
          <SelectOption label="No" value="no" />
        </Select>
        {scrapReworkEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <Label tag="textsmall" marginTop={20}>
                Scrap
              </Label>
              <Slider
                id="OPERATION_SCRAP"
                defaultValue={form.OPERATION_SCRAP.value}
                min={0}
                max={100}
                sliderType="minimized"
                onChange={(event) => handleScrapReworkChange(event)}
              />
            </div>
            <div>
              <Label tag="textsmall" marginTop={20}>
                Rework
              </Label>
              <Slider
                id="OPERATION_REWORK"
                defaultValue={form.OPERATION_REWORK.value}
                min={0}
                max={100}
                sliderType="minimized"
                onChange={(event) => handleScrapReworkChange(event)}
              />
            </div>
          </div>
        )}
        {/*    {<pre>{JSON.stringify(form, null, 2)}</pre>} */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            marginTop: 30,
            backgroundColor: 'white',
            paddingBottom: 20,
            paddingTop: 20,
            width: `calc(100% - 40px)`,
            borderTop: 'solid 1px #ededed',
          }}
        >
          <Stack direction="row" distribute="space-around">
            <Button
              buttonType="button"
              type="primaryOutline"
              width="200px"
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
            <Button buttonType="submit" type="primaryNoShadow" width="200px">
              Update
            </Button>
          </Stack>
        </div>
      </div>
    </form>
  );
};

export default FormOperations;
